// src/components/SmsTilKunden.jsx
// De siste SMS-ene til kunden med leveringsstatus, og et varsel når den siste
// ikke ble levert («Send på nytt»). Brukes i booking, verkstedordre og tilbud.
//
// Statusen hentes på nytt hvert 5. sekund i ett minutt etter at ansatt-appen
// har sendt en SMS (SMS_SENDT_EVENT), og så lenge en SMS er «Sendt» og under
// to minutter gammel, så «Sendt» blir «Levert» uten å laste siden på nytt.
// Ellers hvert 30. sekund (SMS som serveren sender, f.eks. ved statusendring)
// og når fanen blir synlig igjen.
import React, { useCallback, useEffect, useRef, useState } from "react";
import SmsStatusMerke from "./SmsStatusMerke";
import { SMS_SENDT_EVENT, smsVisningsstatus, smsFeilgrunn, kortSmsTid, meldSmsSendt } from "../utils/smsStatus";
import { normalizePhone } from "../utils/format";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ANTALL = 3;
const INTERVALL_MS = 5000;
const POLL_ETTER_SENDING_MS = 60 * 1000;
const UNG_SMS_MS = 2 * 60 * 1000;
const ROLIG_INTERVALL_MS = 30 * 1000;

function forkort(tekst, maks = 70) {
  const t = String(tekst || "").replace(/\s+/g, " ").trim();
  return t.length > maks ? `${t.slice(0, maks - 1)}…` : t;
}

export default function SmsTilKunden({ telefon, tittel = "SMS til kunden" }) {
  const tel = normalizePhone(telefon || "");
  const [smser, setSmser] = useState([]);
  const [lastet, setLastet] = useState(false);
  const [sender, setSender] = useState(false);
  const [melding, setMelding] = useState("");
  const pollTil = useRef(0);
  const sistHentet = useRef(0);

  const hent = useCallback(async () => {
    if (!tel) return;
    sistHentet.current = Date.now();
    try {
      const r = await fetch(`${API_BASE_URL}/smsarchives?telefonnummer=${encodeURIComponent(tel)}&limit=${ANTALL}`);
      if (r.ok) {
        // Filtrer og sorter også her: en eldre backend ignorerer filtrene.
        const liste = (await r.json())
          .filter((s) => normalizePhone(s.telefonnummer || "") === tel)
          .sort((a, b) => new Date(b.sendtDato || 0) - new Date(a.sendtDato || 0))
          .slice(0, ANTALL);
        setSmser(liste);
      }
    } catch {
      /* prøver igjen ved neste runde */
    } finally {
      setLastet(true);
    }
  }, [tel]);

  useEffect(() => {
    setSmser([]);
    setLastet(false);
    hent();
  }, [hent]);

  // Etter sending: hent nå og hvert 5. sekund i ett minutt.
  useEffect(() => {
    const vedSending = () => {
      pollTil.current = Date.now() + POLL_ETTER_SENDING_MS;
      setTimeout(hent, 1500);
    };
    const vedSynlig = () => document.visibilityState === "visible" && hent();
    window.addEventListener(SMS_SENDT_EVENT, vedSending);
    document.addEventListener("visibilitychange", vedSynlig);
    return () => {
      window.removeEventListener(SMS_SENDT_EVENT, vedSending);
      document.removeEventListener("visibilitychange", vedSynlig);
    };
  }, [hent]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const harUngSendt = smser.some(
        (s) => smsVisningsstatus(s).type === "sendt" && Date.now() - new Date(s.sendtDato).getTime() < UNG_SMS_MS
      );
      if (Date.now() < pollTil.current || harUngSendt || Date.now() - sistHentet.current >= ROLIG_INTERVALL_MS) hent();
    }, INTERVALL_MS);
    return () => clearInterval(id);
  }, [hent, smser]);

  const sendPaNytt = async (sms) => {
    setSender(true);
    setMelding("");
    try {
      const r = await fetch(`${API_BASE_URL}/smsarchives/${sms._id}/send-pa-nytt`, { method: "POST" });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setMelding("SMS-en er sendt på nytt.");
        meldSmsSendt();
      } else {
        setMelding(data.message || "Kunne ikke sende SMS-en på nytt.");
      }
    } catch {
      setMelding("Kunne ikke sende SMS-en på nytt. Sjekk nettet og prøv igjen.");
    } finally {
      setSender(false);
      hent();
    }
  };

  if (!tel || (lastet && smser.length === 0)) return null;

  const siste = smser[0];
  const sisteIkkeLevert = siste && siste.status === "failed" && !siste.sendtPaNytt;

  return (
    <section className="mb-4 min-w-0" aria-label={tittel}>
      <div role="status" aria-live="polite">
        {sisteIkkeLevert && (
          <div className="mb-3 flex gap-3 rounded-lg border border-red-400 bg-red-50 p-3 text-red-900">
            <svg viewBox="0 0 16 16" className="mt-0.5 h-5 w-5 flex-none" aria-hidden="true">
              <path fill="currentColor" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm2.9 8.3-1.1 1.1L8 9.1l-1.8 1.8-1.1-1.1L6.9 8 5.1 6.2l1.1-1.1L8 6.9l1.8-1.8 1.1 1.1L9.1 8z" />
            </svg>
            <div className="min-w-0">
              <p className="font-semibold">Kunden fikk ikke siste SMS</p>
              <p className="text-sm break-words">
                «{forkort(siste.meldingstekst)}» ble ikke levert ({kortSmsTid(siste.sendtDato)}).
                {smsFeilgrunn(siste).grunn !== "Ikke levert" && ` ${smsFeilgrunn(siste).grunn}.`}
              </p>
              <button
                type="button"
                onClick={() => sendPaNytt(siste)}
                disabled={sender}
                className="mt-2 rounded-md bg-blue-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-2 disabled:opacity-60"
              >
                {sender ? "Sender …" : "Send på nytt"}
              </button>
            </div>
          </div>
        )}
        {melding && <p className="mb-2 text-sm text-gray-800">{melding}</p>}
      </div>

      <details className="rounded-lg border border-gray-300 bg-white" open={sisteIkkeLevert || undefined}>
        <summary className="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-gray-800 flex flex-wrap items-center gap-2">
          {tittel}
          {siste && <SmsStatusMerke sms={siste} />}
        </summary>
        <ul className="divide-y divide-gray-200 px-3">
          {smser.map((s) => (
            <li key={s._id} className="flex flex-wrap items-start justify-between gap-2 py-2">
              <span className="min-w-0 flex-1 basis-60 text-sm text-gray-800 break-words">{forkort(s.meldingstekst, 140)}</span>
              <span className="flex flex-col items-start gap-0.5">
                <SmsStatusMerke sms={s} visGrunn />
                <span className="text-xs text-gray-600">Sendt {kortSmsTid(s.sendtDato)}</span>
              </span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
